import { useEffect, useState } from 'react';
import { Pencil, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/auth/auth-store';
import { useDoctorsList } from '@/settings/services';
import type { DoctorList } from '@/types/settings-api';
import { PractitionerAddSheet } from './practitioner-add-sheet';

/**
 * Onboarding wizard tab 3 — Add Practitioners (PROD-872, epic PROD-856).
 *
 * Flutter parity: `practice_setup_module/practice_setup/presentation/widgets/
 * doctors/add_doctors_widget.dart`. Lists existing practitioners and opens
 * the Add sub-wizard via slide-in Sheet (see `practitioner-add-sheet.tsx`).
 *
 * Wizard Next gate: enables when `practitioners.length > 0`. Mirrors
 * Flutter `:211-213` — no role/specialities checks at the wizard level.
 *
 * Reuses:
 *   - `useDoctorsList(orgId, false)` from settings — same hook the
 *     settings page uses, so the wizard's list = the settings list.
 *   - `<PractitionerAddSheet>` — new in this PR, will also be wired
 *     into Settings → Practitioners → Add (replacing the existing stub
 *     at `practitioner-edit-page.tsx:96-105`) in a follow-up.
 *
 * Edit flow scope (PR 4): Edit button on each row is a placeholder. The
 * full 8-tab practitioner edit page lives at `/settings/practitioner/{id}`
 * and is reachable after the wizard finishes via Settings → Practitioners.
 * Wizard-local edit would require a router context the wizard doesn't
 * have, so the user completes onboarding with new practitioners and
 * polishes per-tab details post-wizard.
 */
export interface PractitionersStepProps {
  /** Called when at least one practitioner exists — gates the wizard's Next button. */
  enableNext: () => void;
}

export function PractitionersStep({ enableNext }: PractitionersStepProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const { data: practitioners = [], isLoading } = useDoctorsList(orgId, false);

  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (practitioners.length > 0) {
      enableNext();
    }
  }, [practitioners.length, enableNext]);

  return (
    <div className="mx-auto max-w-[720px] px-8 py-10">
      <h1 className="[font:var(--type-card-title)] text-[var(--ink-body)]">
        Add Practitioners
      </h1>
      <p className="mt-4 [font:var(--type-body)] text-[var(--ink-meta)]">
        Add at least one practitioner who will provide services at your practice.
        <br />
        Fields marked with a red dot are mandatory.
      </p>

      <div className="mt-10 space-y-3">
        {isLoading ? (
          <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-10 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
            Loading practitioners…
          </div>
        ) : practitioners.length === 0 ? (
          <Button
            type="button"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Practitioner
          </Button>
        ) : (
          <>
            {practitioners.map((p) => (
              <PractitionerRow
                key={p.doctorSetupId ?? p.emailId ?? Math.random()}
                practitioner={p}
              />
            ))}
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="block w-full rounded-card border border-dashed border-[var(--surface-line)] bg-[var(--surface-read)] px-4 py-4 text-center text-md font-semibold text-[var(--accent-primary)] transition-colors hover:bg-[var(--color-blue-100)]"
            >
              + Add Another Practitioner
            </button>
          </>
        )}
      </div>

      <PractitionerAddSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
    </div>
  );
}

function PractitionerRow({ practitioner }: { practitioner: DoctorList }) {
  const fullName =
    `${practitioner.firstName ?? ''} ${practitioner.lastName ?? ''}`.trim() ||
    'Untitled practitioner';
  const specialty = practitioner.speciality?.code?.displayName ?? null;
  const practiceNames =
    practitioner.practiceList
      ?.map((p) => p.practiceName?.trim())
      .filter((name): name is string => !!name && name.length > 0)
      .join(', ') ?? '';
  const tail = practitioner.isLastLogin
    ? `Last Active ${formatLastActive(practitioner.isLastLogin)}`
    : practitioner.invitationStatus
      ? ''
      : 'Pending invite';
  const metaLine = [practiceNames, practitioner.emailId ?? '', tail]
    .filter((s) => s && s.length > 0)
    .join(' • ');

  return (
    <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-4 py-3 flex items-center gap-4">
      {/* Wire key is `photoURL` — Flutter `doctors_list_model.dart:124`. */}
      <Avatar photoUrl={practitioner.photoURL} name={fullName} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[var(--ink-body)] truncate">
            {fullName}
          </span>
          {practitioner.isSuperAdmin && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--accent-primary)]">
              <ShieldCheck className="h-3 w-3" />
              Super admin
            </span>
          )}
        </div>
        <div className="[font:var(--type-body)] text-[var(--ink-meta)] truncate mt-1">
          {specialty ?? 'N/A'}
        </div>
        {metaLine && (
          <div className="[font:var(--type-body)] text-[var(--ink-meta)] truncate mt-1">
            {metaLine}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled
        title="Edit available from Settings → Practitioners after finishing onboarding"
      >
        <Pencil className="h-4 w-4 mr-1" />
        Edit
      </Button>
    </div>
  );
}

function Avatar({
  photoUrl,
  name,
}: {
  photoUrl: string | null | undefined;
  name: string;
}) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="h-10 w-10 rounded-full object-cover flex-shrink-0"
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="h-10 w-10 rounded-full bg-[var(--color-surface-background)] text-[var(--ink-meta)] flex items-center justify-center text-[length:var(--type-meta-size)] font-semibold flex-shrink-0">
      {initials || '?'}
    </div>
  );
}

function formatLastActive(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
